import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ccp_token";
const USER_KEY = "ccp_user";

// Auth agora vive em cookie HttpOnly (não legível por JS) — o localStorage
// guarda apenas o perfil do usuário para renderização da UI.
export function setSession(user) {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}
export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}
export function getUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}
export async function logout() {
  try {
    await api.post("/auth/logout");
  } catch (err) {
    // best-effort — o cookie expira sozinho
    console.debug("logout best-effort falhou:", err?.message);
  }
  clearSession();
}

export const api = axios.create({ baseURL: API, withCredentials: true });

// -----------------------------------------------------------------------
// PIN de colaborador — o interceptor abaixo detecta 428 (falta PIN) e
// 401 relacionados a PIN e chama o prompter global registrado pelo
// CollabPinProvider. Isso evita ter que envolver TODA chamada de escrita
// com um try/catch manual: o interceptor abre o modal, injeta os headers
// e reenvia a requisição de forma transparente.
// -----------------------------------------------------------------------
let _pinPrompter = null;
export function registerPinPrompter(fn) {
  _pinPrompter = fn;
}

// Descreve a ação para o modal com base no método+url — mantém a UX
// legível ("Salvar em /customers" fica esquisito). Traduz para verbo em
// PT-BR curto.
function _describeAction(config) {
  const method = (config?.method || "get").toUpperCase();
  const url = config?.url || "";
  const verb = { POST: "criar", PUT: "salvar", PATCH: "salvar", DELETE: "excluir" }[method] || "alterar";
  const map = [
    [/\/customers/, "cliente"],
    [/\/suppliers/, "fornecedor"],
    [/\/products/, "produto"],
    [/\/cup-colors/, "cor de copo"],
    [/\/silk-colors/, "cor de silk"],
    [/\/collaborators/, "colaborador"],
    [/\/stations/, "estação"],
    [/\/bills/, "conta a pagar"],
    [/\/payroll/, "folha de pagamento"],
    [/\/orders\/manual/, "pedido manual"],
    [/\/orders\/bulk-delete/, "exclusão em lote"],
    [/\/orders\/.+\/restore/, "restauração do pedido"],
    [/\/orders\/archive-old/, "arquivamento em massa"],
    [/\/orders\/.+\/exit-date/, "data limite do pedido"],
    [/\/orders\/.+\/production/, "produção do pedido"],
    [/\/orders\/.+\/items/, "itens do pedido"],
    [/\/orders\/.+\/unarchive/, "reativação do pedido"],
    [/\/orders\/maintenance/, "manutenção de pedidos"],
    [/\/bling/, "configuração do Bling"],
    [/\/backup/, "backup"],
    [/\/push/, "notificações"],
    [/\/queue-token/, "token da fila"],
    [/\/inventory/, "estoque"],
  ];
  for (const [rx, label] of map) if (rx.test(url)) return `${verb} ${label}`;
  return verb;
}


api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config || {};
    const detail = err?.response?.data?.detail || "";

    // ----- Backend fora do ar (deploy/atualização ou queda de rede) -----
    // Sem response (rede/resposta vazia) ou erro de gateway (502/503/504,
    // 520-524 do Cloudflare) → mostra o selo "Sistema atualizando", espera o
    // /api/health voltar e REENVIA a requisição original automaticamente.
    const gatewayDown = !err?.response || [502, 503, 504, 520, 521, 522, 523, 524].includes(status);
    if (gatewayDown && err?.code !== "ERR_CANCELED" && (cfg._downRetries || 0) < 2) {
      cfg._downRetries = (cfg._downRetries || 0) + 1;
      window.dispatchEvent(new CustomEvent("backend-offline"));
      await new Promise((resolve) => {
        const onBack = () => {
          window.removeEventListener("backend-online", onBack);
          resolve();
        };
        window.addEventListener("backend-online", onBack);
      });
      return api.request(cfg);
    }

    // ----- Fluxo do PIN de colaborador -----
    // 428 = header X-Collab-Code/Password ausente → sempre abre o modal.
    // 401 com detail mencionando "colaborador" = senha incorreta → reabre.
    const needsPin = status === 428 || (status === 401 && /colaborador/i.test(String(detail)));
    if (needsPin && _pinPrompter && (cfg._pinAttempts || 0) < 3) {
      cfg._pinAttempts = (cfg._pinAttempts || 0) + 1;
      const label = _describeAction(cfg);
      const pin = await _pinPrompter(label);
      if (pin) {
        cfg.headers = cfg.headers || {};
        cfg.headers["X-Collab-Code"] = pin.code;
        cfg.headers["X-Collab-Password"] = pin.password;
        return api.request(cfg);
      }
      // Usuário cancelou → propaga o erro original
      return Promise.reject(err);
    }

    if (status === 401) {
      // 401 de /move e /inventory/adjust é erro de credencial do COLABORADOR (validação de negócio), não sessão expirada
      const url = err?.config?.url || "";
      const isCollabAuth = /\/move$|\/inventory\/adjust$/.test(url);
      const path = window.location.pathname;
      if (!isCollabAuth && path !== "/login" && path !== "/queue") {
        clearSession();
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const STATUS_LABELS = {
  em_desenvolvimento: "Em Desenvolvimento",
  separacao: "Separação",
  gravacao_tela: "Gravação de Tela",
  serigrafia: "Serigrafia",
  esteira: "Esteira",
  expedicao: "Expedição",
  enviado: "Enviado",
};

export const STATUS_ORDER = [
  "em_desenvolvimento",
  "separacao",
  "gravacao_tela",
  "serigrafia",
  "esteira",
  "expedicao",
  "enviado",
];

export function fmtBRL(v) {
  const n = Number(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function fmtInt(v) {
  return Number(v || 0).toLocaleString("pt-BR");
}

/**
 * Calcula a quantidade total de copos de um pedido usando o campo mais
 * confiável disponível. Ordem de precedência:
 *  1) `total_cups` (Bling sync — reflete os `order_items` do SKU parser)
 *  2) Soma de `production_items[].qty` (preenchido pelo dev na fábrica)
 *  3) Soma de `order_items[].cups` (fallback final)
 *
 * Isso resolve a discrepância antiga entre Kanban / Pedidos / Painel Fábrica,
 * que mostravam quantidades diferentes quando um deles usava outro campo.
 */
export function orderCupsCount(o) {
  if (!o) return 0;
  if (o.total_cups) return Number(o.total_cups) || 0;
  const fromProd = (o.production_items || []).reduce(
    (a, b) => a + (parseInt(b.qty, 10) || 0),
    0,
  );
  if (fromProd) return fromProd;
  const fromOrder = (o.order_items || []).reduce(
    (a, b) => a + (Number(b.cups) || 0),
    0,
  );
  return fromOrder;
}


// -------------------------------------------------------------------------
// Datas/horas em horário de Brasília
// -------------------------------------------------------------------------
// O backend grava tudo em UTC (`+00:00`). No display convertemos para
// `America/Sao_Paulo` para o usuário sempre ver o horário local, mesmo
// quando o servidor está em outro fuso.

const BR_TZ = "America/Sao_Paulo";

/**
 * Parseia uma ISO string ASSUMINDO UTC quando o timezone estiver ausente.
 * Bug clássico: `new Date("2026-07-22T19:48:00")` (sem sufixo) o browser
 * interpreta como HORA LOCAL, não UTC — daí exibe errado em qualquer fuso.
 * Aqui SEMPRE adicionamos "Z" se não houver marcador de timezone.
 */
function _parseAsUtc(input) {
  if (input instanceof Date) return input;
  const s = String(input || "").trim();
  if (!s) return null;
  // Tem timezone marker? (Z, ou ±HH:MM, ou ±HHMM)
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasTz ? s : s + "Z");
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Recebe uma ISO string (ou Date) em UTC e devolve "DD/MM/YYYY HH:mm" no
 * horário de Brasília. Se receber string sem timezone, ASSUME UTC (é o
 * padrão do backend). Retorna "—" se input inválido/vazio.
 */
export function fmtBRDateTime(input, opts = {}) {
  const d = _parseAsUtc(input);
  if (!d) return "—";
  const withSeconds = opts.seconds ? { second: "2-digit" } : {};
  return d.toLocaleString("pt-BR", {
    timeZone: BR_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...withSeconds,
  });
}

/** Só a data (DD/MM/YYYY) no fuso de Brasília. */
export function fmtBRDate(input) {
  const d = _parseAsUtc(input);
  if (!d) return "—";
  return d.toLocaleDateString("pt-BR", { timeZone: BR_TZ });
}

/** Só a hora (HH:mm) no fuso de Brasília. */
export function fmtBRTime(input) {
  const d = _parseAsUtc(input);
  if (!d) return "—";
  return d.toLocaleTimeString("pt-BR", { timeZone: BR_TZ, hour: "2-digit", minute: "2-digit" });
}

/** Retorna a data de HOJE (YYYY-MM-DD) usando o fuso de Brasília. */
export function todayBR() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: BR_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  const d = parts.find((p) => p.type === "day").value;
  return `${y}-${m}-${d}`;
}


/**
 * Prazo de desenvolvimento: 5 dias corridos após o pedido entrar no sistema.
 * Só se aplica enquanto production_status === "em_desenvolvimento".
 * Retorna { applies, days_remaining, days_overdue } onde apenas UMA das duas
 * contagens é > 0. Se applies=false, o pedido já saiu de dev.
 */
export const DEV_DEADLINE_DAYS = 5;

// Busca unificada de pedidos (client-side): nº do pedido (inclui manuais #M0001),
// cliente, telefone/contato (com ou sem máscara) e "Pedido na Loja" (Shopee/ML).
export function orderMatchesSearch(o, rawSearch) {
  if (!rawSearch) return true;
  const q = rawSearch.trim().toLowerCase().replace(/^#/, "");
  if (!q) return true;
  if ((o.bling_number || "").toLowerCase().includes(q)) return true;
  if ((o.customer || "").toLowerCase().includes(q)) return true;
  if ((o.store_number || "").toLowerCase().includes(q)) return true;
  if ((o.customer_phone || "").toLowerCase().includes(q)) return true;
  const digits = q.replace(/\D/g, "");
  if (digits.length >= 6 && (o.customer_phone || "").replace(/\D/g, "").includes(digits)) return true;
  return false;
}


export function devCountdown(o) {
  const applies = (o?.production_status || "em_desenvolvimento") === "em_desenvolvimento";
  if (!applies || !o?.created_at) {
    return { applies: false, days_remaining: 0, days_overdue: 0 };
  }
  const created = new Date(o.created_at);
  if (isNaN(created.getTime())) return { applies: false, days_remaining: 0, days_overdue: 0 };
  const now = new Date();
  const elapsedMs = now.getTime() - created.getTime();
  const elapsedDays = elapsedMs / 86400000;
  const remaining = DEV_DEADLINE_DAYS - elapsedDays;
  if (remaining >= 0) {
    return { applies: true, days_remaining: Math.ceil(remaining), days_overdue: 0 };
  }
  return { applies: true, days_remaining: 0, days_overdue: Math.ceil(-remaining) };
}
